import { NgFor } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import {
  AbstractDialog,
  AbstractDialogData,
  AbstractDialogResultData,
  DialogComponent,
} from '@next/core-lib/dialog';
import { AgrNumberDirective } from '@next/core-lib/form';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';

import { AnimalGroupBarnMapping } from 'src/app/cycle/models/cycle.model';
import Utils from 'src/app/shared/utils';

import { EditAnimalGroupMode, SwitchDayDataRow } from '../animal-group.model';

export interface SwitchDayEditDialogInputData extends AbstractDialogData {
  switchDayRow?: SwitchDayDataRow;
  mode: EditAnimalGroupMode;
  barnMappings: AnimalGroupBarnMapping[];
}

export interface SwitchDayEditDialogResultData {
  switchDayRow: SwitchDayDataRow;
}

@Component({
  selector: 'app-switch-day-edit.component',
  templateUrl: 'switch-day-edit.component.html',
  imports: [
    DialogComponent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    NgFor,
    MatOption,
    MatInput,
    AgrNumberDirective,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    TranslatePipe,
  ],
})
export class SwitchDayEditComponent
  extends AbstractDialog<SwitchDayEditDialogResultData>
  implements OnInit
{
  data = inject<SwitchDayEditDialogInputData>(MAT_DIALOG_DATA);
  protected mdDialogRef =
    inject<MatDialogRef<SwitchDayEditComponent, AbstractDialogResultData>>(MatDialogRef);
  private readonly localeService = inject(LocaleService);

  id: number;

  formGroup = new FormGroup({
    barn: new FormControl<AnimalGroupBarnMapping>(null, [Validators.required]),
    phaseFrom: new FormControl<number>(null, [Validators.required]),
    phaseTo: new FormControl<number>(null, [Validators.required]),
    date: new FormControl<Date>(null, [Validators.required]),
  });

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    super();
    this.data.title = this.localeService.translate(this.data.mode);
  }

  ngOnInit() {
    if (this.data.mode === EditAnimalGroupMode.Edit && this.data.switchDayRow !== null) {
      const selectedBarn = this.data.barnMappings.find(
        (b) => b.barnId === this.data.switchDayRow?.barn?.barnId,
      );

      this.formGroup.controls.barn.setValue(selectedBarn);
      this.formGroup.controls.phaseFrom.setValue(this.data.switchDayRow.phaseFrom);
      this.formGroup.controls.phaseTo.setValue(this.data.switchDayRow.phaseTo);
      this.formGroup.controls.date.setValue(new Date(this.data.switchDayRow.date));
    }
  }

  save() {
    this.saveSwitchDay();
  }

  private saveSwitchDay() {
    if (this.formGroup.valid) {
      super.confirm({
        switchDayRow: {
          id: this.data.switchDayRow?.id,
          barn: this.formGroup.controls.barn.value,
          date: Utils.parseDateTimeFromFormValue(this.formGroup.controls.date.value),
          phaseFrom: this.formGroup.controls.phaseFrom.value,
          phaseTo: this.formGroup.controls.phaseTo.value,
        },
      });
    }
  }
}
